"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useQuery } from "convex/react";
import type { Route } from "next";
import { useParams } from "next/navigation";
import type { ReactNode } from "react";
import { MonthlyProjectsChart, OpenWorkflowChart } from "@/components/dashboard/project-charts";
import { Section } from "@/components/ui/section";
import { Skeleton } from "@/components/ui/skeleton";
import { StatGrid, StatTile } from "@/components/ui/stat";
import { buildOrganizationPath } from "@/lib/organization-path";
interface Stat {
  label: string;
  value: string | number;
  hint?: string;
  /** Organization-relative list path the tile opens. */
  path?: string;
}

/** Placeholder for values that are not loaded, e.g. without an active subscription. */
const NO_VALUE = "-";
function StatSkeleton(t0) {
  const $ = _c(6);
  const {
    withHint: t1
  } = t0;
  const withHint = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="h-3 w-24" />;
    t3 = <Skeleton className="h-6 w-12" />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== withHint) {
    t4 = withHint ? <Skeleton className="h-3 w-40" /> : null;
    $[2] = withHint;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <div className="flex flex-col gap-1.5 border-l pl-3">{t2}{t3}{t4}</div>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}

/** Section pair for the two project charts; side by side on wide screens. */
function ChartSections(t0) {
  const $ = _c(7);
  const {
    workflow,
    monthly
  } = t0;
  let t1;
  if ($[0] !== workflow) {
    t1 = <Section title="Andamento dos projetos abertos">{workflow}</Section>;
    $[0] = workflow;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== monthly) {
    t2 = <Section title={"Novos projetos por m\xEAs"}>{monthly}</Section>;
    $[2] = monthly;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="grid gap-6 lg:grid-cols-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Organization dashboard summary: headline counts, the "Foco operacional" section and the
 * project charts. Renders siblings so the parent `SectionStack` separates them with a rule.
 */
export function DashboardOverview(t0) {
  const $ = _c(51);
  const {
    subscriptionLocked: t1
  } = t0;
  const subscriptionLocked = t1 === undefined ? false : t1;
  const {
    organizationSlug
  } = useParams();
  let t2;
  if ($[0] !== subscriptionLocked) {
    t2 = subscriptionLocked ? "skip" : {};
    $[0] = subscriptionLocked;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const producerStats = useQuery(api.producers.queries.getProducerStats, t2);
  let t3;
  if ($[2] !== subscriptionLocked) {
    t3 = subscriptionLocked ? "skip" : {};
    $[2] = subscriptionLocked;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const projectDashboard = useQuery(api.projects.queries.getProjectsDashboard, t3);
  const isLoading = !subscriptionLocked && (producerStats === undefined || projectDashboard === undefined);
  if (isLoading) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div aria-busy="true" className="grid grid-cols-2 gap-4 lg:grid-cols-4">{Array.from({
          length: 4
        }).map(_temp)}</div>;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Section title="Foco operacional"><div aria-busy="true" className="grid grid-cols-2 gap-4 lg:grid-cols-4">{Array.from({
            length: 2
          }).map(_temp2)}</div></Section>;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <ChartSections monthly={<Skeleton aria-busy="true" className="h-[8.5rem] w-full" />} workflow={<Skeleton aria-busy="true" className="h-[8.125rem] w-full" />} />;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== t4 || $[8] !== t5) {
      t7 = <>{t4}{t5}{t6}</>;
      $[7] = t4;
      $[8] = t5;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  let t4;
  if ($[10] !== subscriptionLocked) {
    t4 = value => subscriptionLocked ? NO_VALUE : value;
    $[10] = subscriptionLocked;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const show = t4;
  const totalProducers = producerStats?.total ?? 0;
  const activeProducers = producerStats?.active ?? 0;
  const totalProjects = projectDashboard?.totalProjects ?? 0;
  const openProjects = projectDashboard?.openProjects ?? 0;
  const completedProjects = projectDashboard?.completedProjects ?? 0;
  const canceledProjects = projectDashboard?.canceledProjects ?? 0;
  const recentlyCreatedProducers = producerStats?.recentActivity?.recentlyCreated ?? 0;
  const closedProjects = completedProjects + canceledProjects;
  let t5;
  if ($[12] !== closedProjects || $[13] !== completedProjects) {
    t5 = closedProjects > 0 ? `${Math.round(completedProjects / closedProjects * 100)}%` : NO_VALUE;
    $[12] = closedProjects;
    $[13] = completedProjects;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const completionRate = t5;
  let T0;
  let T1;
  let t6;
  let t7;
  let t8;
  let tileHref;
  if ($[15] !== activeProducers || $[16] !== canceledProjects || $[17] !== completedProjects || $[18] !== completionRate || $[19] !== openProjects || $[20] !== organizationSlug || $[21] !== recentlyCreatedProducers || $[22] !== show || $[23] !== subscriptionLocked || $[24] !== totalProducers || $[25] !== totalProjects) {
    const stats = [{
      label: "Produtores ativos",
      value: show(activeProducers),
      hint: totalProducers !== activeProducers ? `de ${totalProducers} no total` : undefined,
      path: "/producers?status=active"
    }, {
      label: "Projetos abertos",
      value: show(openProjects),
      hint: totalProjects !== openProjects ? `de ${totalProjects} no total` : undefined,
      path: "/projects?status=open"
    }, {
      label: "Projetos conclu\xEDdos",
      value: show(completedProjects),
      path: "/projects?status=completed"
    }, {
      label: "Projetos cancelados",
      value: show(canceledProjects),
      path: "/projects?status=canceled"
    }];
    const focusItems = [{
      label: "Novos produtores",
      value: show(recentlyCreatedProducers),
      hint: "\xFAltimos 30 dias"
    }, {
      label: "Taxa de conclus\xE3o",
      value: show(completionRate),
      hint: "dos projetos encerrados"
    }];
    let t9;
    if ($[32] !== organizationSlug || $[33] !== subscriptionLocked) {
      t9 = path => path && organizationSlug && !subscriptionLocked ? buildOrganizationPath(organizationSlug, path) as Route : undefined;
      $[32] = organizationSlug;
      $[33] = subscriptionLocked;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    tileHref = t9;
    const t10 = stats.map(stat => <StatTile hint={stat.hint} href={tileHref(stat.path)} key={stat.label} label={stat.label} value={stat.value} />);
    if ($[35] !== t10) {
      t8 = <StatGrid>{t10}</StatGrid>;
      $[35] = t10;
      $[36] = t8;
    } else {
      t8 = $[36];
    }
    T1 = Section;
    t7 = "Foco operacional";
    T0 = StatGrid;
    t6 = focusItems.map(item => <StatTile hint={item.hint} href={tileHref(item.path)} key={item.label} label={item.label} value={item.value} />);
    $[15] = activeProducers;
    $[16] = canceledProjects;
    $[17] = completedProjects;
    $[18] = completionRate;
    $[19] = openProjects;
    $[20] = organizationSlug;
    $[21] = recentlyCreatedProducers;
    $[22] = show;
    $[23] = subscriptionLocked;
    $[24] = totalProducers;
    $[25] = totalProjects;
    $[26] = T0;
    $[27] = T1;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = tileHref;
  } else {
    T0 = $[26];
    T1 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    tileHref = $[31];
  }
  let t9;
  if ($[37] !== T0 || $[38] !== t6) {
    t9 = <T0>{t6}</T0>;
    $[37] = T0;
    $[38] = t6;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== T1 || $[41] !== t7 || $[42] !== t9) {
    t10 = <T1 title={t7}>{t9}</T1>;
    $[40] = T1;
    $[41] = t7;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== projectDashboard || $[45] !== tileHref) {
    t11 = projectDashboard ? <ChartSections monthly={<MonthlyProjectsChart months={projectDashboard.createdByMonth} />} workflow={<OpenWorkflowChart hrefFor={workflow => workflow === "unrecorded" ? undefined : tileHref(`/projects?status=open&workflowStatus=${workflow}`)} rows={projectDashboard.openByWorkflow} />} /> : null;
    $[44] = projectDashboard;
    $[45] = tileHref;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== t10 || $[48] !== t11 || $[49] !== t8) {
    t12 = <>{t8}{t10}{t11}</>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t8;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  return t12;
}
function _temp2(__0, index_0) {
  return <StatSkeleton key={index_0} withHint={true} />;
}
function _temp(_, index) {
  return <StatSkeleton key={index} />;
}